import { createApi } from "@reduxjs/toolkit/query/react";
import { AxiosError } from "axios";
import { axiosNewPrivate } from "../../util/axios";
import { serializeQueryArgsWithTenant } from "../tenantCacheKey";
import type { UseCase } from "../../util/useCases";

/**
 * M3 (#3036) — one room's (or one divided hall's) week, from
 * `GET /app/rooms/:groupId/week` (new-backend/app). Resolved URL:
 * `REACT_APP_NEW_API_BASE_URL` (…/app) + `rooms/<groupId>/week` →
 * `…/app/rooms/<groupId>/week?from=&to=`.
 *
 * The answer depends on WHO asks (own / own organization / another
 * organization / anonymous), and the caller travels as a bearer header the
 * cache cannot see. Two guards, both needed:
 *   - `viewerId` is part of the argument, so a different account is a
 *     different cache entry by construction;
 *   - the slice is ACCOUNT-SCOPED (`redux/accountScopedCaches.ts`), so a login
 *     or logout evicts it.
 * The tenant is in the key via `serializeQueryArgsWithTenant`.
 */

export type RoomWeekIntervalKind = "own" | "organization" | "other_organization" | "occupied";

export interface RoomWeekInterval {
  kind: RoomWeekIntervalKind;
  /** "all" = the whole room; otherwise a segment id. */
  column: string;
  from: string;
  to: string;
  title?: string | null;
  bookingNumber?: string | null;
  organizationName?: string;
}

export interface RoomWeekSegment {
  id: string;
  name: string;
  groupId: string | null;
  bookable: boolean;
}

export type RoomWeekStructure =
  | { kind: "single"; bookable: boolean }
  | {
      kind: "divided";
      whole: { groupId: string | null; name: string | null; bookable: boolean };
      segments: RoomWeekSegment[];
      openedSegmentId: string | null;
    };

export interface RoomWeekOpeningHours {
  weekday: string;
  fromHour: number;
  toHour: number;
}

/**
 * One occurrence of an allocation window (ADR-0027, W2-1), told from the
 * caller's side (D3b): `own` = the caller's organisation holds it and may book
 * in it; another organisation's window carries its name only when that
 * organisation appears by name, otherwise null ("reserved"). Another
 * organisation's released dates are not sent at all: that time is free.
 */
export interface RoomWeekWindow {
  /** "all" = the whole room; otherwise segment ids (as `RoomWeekInterval.column`). */
  columns: string[];
  date: string;
  from: string;
  to: string;
  own: boolean;
  /** Own windows only: the organisation gave this date back. */
  released: boolean;
  organizationName: string | null;
}

export interface RoomWeekResponse {
  groupId: string;
  name: string | null;
  langNames: Record<string, string> | null;
  useCase: UseCase;
  /**
   * The opened room's live product category (#3035), or null. Optional
   * because a cached bundle can meet an older response without it.
   */
  categoryKey?: string | null;
  timeZone: string;
  from: string;
  to: string;
  location: { name: string | null; city: string | null; district: string | null };
  openingHours: RoomWeekOpeningHours[];
  structure: RoomWeekStructure;
  intervals: RoomWeekInterval[];
  /** Optional: a cached bundle can meet an older response without it. */
  windows?: RoomWeekWindow[];
}

export interface RoomWeekArgs {
  groupId: string;
  from: string;
  to: string;
  /** The signed-in account id, or null — part of the cache key on purpose. */
  viewerId: string | null;
}

export const roomWeekApiSlice = createApi({
  reducerPath: "roomWeekApiSlice",
  // Every call goes through the queryFn below; the base query is never hit.
  baseQuery: async () => ({ data: undefined }),
  serializeQueryArgs: serializeQueryArgsWithTenant,
  // "RoomWeek": a released hall time (W2-1, `accountApiSlice`) changes who may
  // book it, so the weeks are refetched.
  tagTypes: ["RoomWeek"],
  endpoints: (builder) => ({
    getRoomWeek: builder.query<RoomWeekResponse, RoomWeekArgs>({
      providesTags: ["RoomWeek"],
      queryFn: async ({ groupId, from, to }) => {
        try {
          const result = await axiosNewPrivate.get<RoomWeekResponse>(
            `rooms/${encodeURIComponent(groupId)}/week`,
            { params: { from, to } }
          );
          return { data: result.data };
        } catch (err) {
          const axiosErr = err as AxiosError<{ message?: string }>;
          return {
            error: {
              status: axiosErr.response?.status ?? "FETCH_ERROR",
              data: axiosErr.response?.data ?? { message: axiosErr.message },
            },
          };
        }
      },
    }),
  }),
});

export const { useGetRoomWeekQuery } = roomWeekApiSlice;
